/**
 * "Watch locally" QR widget — pinned to a corner of a game's OBS browser
 * source. Encodes a URL like `https://watch.thegamer.network/<handle>` so
 * stream viewers (and the streamer's local audience) can scan and follow the
 * game from their own device without leaving OBS.
 *
 * Mount once per browser-source SPA, AFTER the streamer's handle is known
 * (typically inside the `auth_ok` WS frame handler). The widget hides itself
 * when `handle` is `null` — a streamer who hasn't claimed a handle yet has no
 * useful URL to encode. Call `update(handle)` to re-render if the handle
 * changes after first mount.
 *
 * Viewer-mode iframes (loaded by the public watch shell) must NOT mount this
 * widget; the watch SPA itself owns the URL. Check the game's existing
 * viewer/spectator flag before calling.
 *
 * Inline styles only — no CSS imports — so every game can drop this in without
 * touching its build/CSS pipeline.
 */

import qrcodeFactory from './index.js';

export type Corner = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

export interface WatchQrOptions {
  /**
   * Streamer's claimed public handle (lowercased). Pass `null` to mount in a
   * hidden state — the widget will become visible once `update(handle)` is
   * called with a non-null value.
   */
  handle: string | null;
  /**
   * Origin of the public watch shell. Defaults to the prod host; override
   * in dev/test or when the watch shell moves domains.
   */
  watchHost?: string;
  /** Corner of the parent to pin the widget to. Default: bottom-right. */
  position?: Corner;
  /** QR pixel side-length inside the dark pill. Default: 130. */
  size?: number;
  /**
   * When true (default), the QR size is computed from the viewport's smaller
   * dimension (`vmin`) at mount and on every window resize, so the widget
   * looks proportionally identical at 1080p OBS, 4K, and inside the small
   * watch-viewer iframe. When false, the fixed `size` value is used verbatim
   * — this preserves the legacy behavior callers/tests may rely on.
   */
  scaleWithViewport?: boolean;
  /** Parent to attach to. Default: document.body. */
  parent?: HTMLElement;
}

export interface WatchQrController {
  /** Tear down the widget and remove it from the DOM. */
  destroy(): void;
  /** Re-render with a new handle (or hide if `null`). */
  update(handle: string | null): void;
}

const DEFAULT_WATCH_HOST = 'https://watch.thegamer.network';
const DEFAULT_SIZE = 130;

// Viewport-scaling constants. At 1920x1080, viewportMin = 1080, so the QR
// renders at ~130px (1080 * 0.12 = 129.6) — matching the legacy fixed default.
// Clamped to [60, 220] so it stays legible in tiny iframes and doesn't bloat
// at 4K+ resolutions.
const VIEWPORT_SCALE = 0.12;
const MIN_VIEWPORT_SIZE = 60;
const MAX_VIEWPORT_SIZE = 220;

function computeViewportSize(): number {
  const vw =
    typeof window === 'undefined' ? 1920 : window.innerWidth || 1920;
  const vh =
    typeof window === 'undefined' ? 1080 : window.innerHeight || 1080;
  const vmin = Math.min(vw, vh);
  const raw = Math.round(vmin * VIEWPORT_SCALE);
  return Math.max(MIN_VIEWPORT_SIZE, Math.min(MAX_VIEWPORT_SIZE, raw));
}

/**
 * Mount the "Watch locally" QR widget. Idempotent per controller — calling
 * `update` swaps the encoded URL without recreating the DOM nodes; `destroy`
 * removes everything.
 */
export function mountWatchQrWidget(opts: WatchQrOptions): WatchQrController {
  const watchHost = (opts.watchHost ?? DEFAULT_WATCH_HOST).replace(/\/+$/, '');
  const position: Corner = opts.position ?? 'bottom-right';
  const fixedSize = Math.max(48, Math.floor(opts.size ?? DEFAULT_SIZE));
  const scaleWithViewport = opts.scaleWithViewport ?? true;
  const parent: HTMLElement = opts.parent ?? document.body;

  // Current resolved size in CSS pixels. Recomputed on resize when
  // scaleWithViewport is true.
  let currentSize = scaleWithViewport ? computeViewportSize() : fixedSize;

  const container = document.createElement('div');
  container.setAttribute('data-testid', 'watch-qr-widget');
  applyContainerStyle(container, position);

  const qrWrap = document.createElement('div');
  applyQrWrapStyle(qrWrap);

  const canvas = document.createElement('canvas');
  canvas.width = currentSize;
  canvas.height = currentSize;
  canvas.style.cssText = `display: block; width: ${currentSize}px; height: ${currentSize}px;`;
  qrWrap.appendChild(canvas);

  const caption = document.createElement('div');
  caption.textContent = 'Watch locally';
  applyCaptionStyle(caption);

  container.appendChild(qrWrap);
  container.appendChild(caption);
  parent.appendChild(container);

  let currentHandle: string | null = null;

  function render(handle: string | null): void {
    currentHandle = handle;
    if (!handle) {
      container.style.display = 'none';
      return;
    }
    container.style.display = 'flex';
    const url = `${watchHost}/${encodeURIComponent(handle)}`;
    renderQr(canvas, url, currentSize);
  }

  // Single named handler so we never double-bind across re-entrant mounts and
  // so destroy() can remove the exact reference. Idempotent: re-running the
  // same logic with an unchanged viewport produces an identical re-render.
  function onResize(): void {
    if (!scaleWithViewport) return;
    const next = computeViewportSize();
    if (next === currentSize) return;
    currentSize = next;
    // Resize the canvas backing store + display size before re-rendering so
    // the QR module grid is re-laid-out at the new pixel budget.
    canvas.width = currentSize;
    canvas.height = currentSize;
    canvas.style.width = `${currentSize}px`;
    canvas.style.height = `${currentSize}px`;
    if (currentHandle) {
      const url = `${watchHost}/${encodeURIComponent(currentHandle)}`;
      renderQr(canvas, url, currentSize);
    }
  }

  if (scaleWithViewport && typeof window !== 'undefined') {
    window.addEventListener('resize', onResize);
  }

  render(opts.handle);

  return {
    destroy(): void {
      if (scaleWithViewport && typeof window !== 'undefined') {
        window.removeEventListener('resize', onResize);
      }
      if (container.parentNode) container.parentNode.removeChild(container);
    },
    update(handle: string | null): void {
      // Skip the redundant re-paint when the handle is unchanged.
      if (handle === currentHandle) return;
      render(handle);
    },
  };
}

function applyContainerStyle(el: HTMLElement, position: Corner): void {
  const corner = cornerOffsets(position);
  // Corner offsets scale with the viewport so the gap between the pill and
  // the screen edge tracks the QR's growth. `max()` floors at 12px so it
  // never collapses to nothing in a tiny iframe.
  el.style.cssText = [
    'position: fixed',
    `bottom: ${corner.bottom}`,
    `top: ${corner.top}`,
    `left: ${corner.left}`,
    `right: ${corner.right}`,
    'background: rgba(0, 0, 0, 0.55)',
    '-webkit-backdrop-filter: blur(4px)',
    'backdrop-filter: blur(4px)',
    'border-radius: clamp(8px, 1.2vmin, 16px)',
    // 1vmin ≈ 10.8px at 1080p, ~9.6px at 720p — close to the legacy 10/8px.
    'padding: clamp(6px, 1vmin, 14px) clamp(6px, 1vmin, 14px) clamp(5px, 0.8vmin, 12px)',
    'display: flex',
    'flex-direction: column',
    'align-items: center',
    'gap: clamp(4px, 0.6vmin, 10px)',
    'z-index: 999999',
    'pointer-events: none',
    'user-select: none',
    'font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    'box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35)',
  ].join('; ');
}

function applyQrWrapStyle(el: HTMLElement): void {
  el.style.cssText = [
    'background: #ffffff',
    'padding: clamp(4px, 0.6vmin, 10px)',
    'border-radius: clamp(4px, 0.6vmin, 10px)',
    'line-height: 0', // strip the inline-block whitespace under the canvas
    'display: block',
  ].join('; ');
}

function applyCaptionStyle(el: HTMLElement): void {
  el.style.cssText = [
    'color: #ffffff',
    'font-size: clamp(10px, 1.1vmin, 16px)',
    'font-weight: 600',
    'letter-spacing: 0.02em',
    'text-align: center',
    'text-shadow: 0 1px 2px rgba(0,0,0,0.4)',
  ].join('; ');
}

// 1.5vmin ≈ 16px at 1080p — matches the legacy fixed offset, but scales with
// the viewport. `max(12px, …)` keeps the pill clear of the very edge inside
// small iframes.
const CORNER_OFFSET = 'max(12px, 1.5vmin)';

function cornerOffsets(position: Corner): {
  top: string;
  right: string;
  bottom: string;
  left: string;
} {
  switch (position) {
    case 'bottom-right':
      return {
        top: 'auto',
        right: CORNER_OFFSET,
        bottom: CORNER_OFFSET,
        left: 'auto',
      };
    case 'bottom-left':
      return {
        top: 'auto',
        right: 'auto',
        bottom: CORNER_OFFSET,
        left: CORNER_OFFSET,
      };
    case 'top-right':
      return {
        top: CORNER_OFFSET,
        right: CORNER_OFFSET,
        bottom: 'auto',
        left: 'auto',
      };
    case 'top-left':
      return {
        top: CORNER_OFFSET,
        right: 'auto',
        bottom: 'auto',
        left: CORNER_OFFSET,
      };
  }
}

/**
 * Render `text` into `canvas` as a QR code sized to fit `size` px. Mirrors the
 * renderer in socialwordle's main.ts — typeNumber 0 = auto-pick smallest type;
 * 'M' error correction = ~15% redundancy, balanced for short URL payloads.
 */
function renderQr(canvas: HTMLCanvasElement, text: string, size: number): void {
  const qr = qrcodeFactory(0, 'M');
  qr.addData(text);
  qr.make();

  const moduleCount = qr.getModuleCount();
  const margin = 1; // 1-cell quiet zone

  const cellSize = Math.max(2, Math.floor(size / (moduleCount + 2 * margin)));
  const pixelSize = (moduleCount + 2 * margin) * cellSize;

  canvas.width = pixelSize;
  canvas.height = pixelSize;
  // Pin display size so the dark pill stays a constant shape even when the
  // underlying pixel grid changes (denser URLs round to a smaller cellSize).
  canvas.style.width = `${size}px`;
  canvas.style.height = `${size}px`;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // White background (quiet zone included).
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, pixelSize, pixelSize);

  // Dark modules.
  ctx.fillStyle = '#000000';
  for (let row = 0; row < moduleCount; row++) {
    for (let col = 0; col < moduleCount; col++) {
      if (qr.isDark(row, col)) {
        ctx.fillRect(
          (col + margin) * cellSize,
          (row + margin) * cellSize,
          cellSize,
          cellSize,
        );
      }
    }
  }
}
